import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import Link from 'next/link'

import {
    ArrowUpRight,
    ChevronRight,
    CircleDollarSign,
    Compass,
    FlagTriangleRight,
    MessageCircleQuestion,
    Send,
    ShieldCheck,
    Store,
    Users
} from 'lucide-react'

import { trackEvent } from '@/lib/analytics/client'
import { DIRECTORY_INDEX_PATH } from '@/lib/seo/telegram-directory'
import { TelegramGlyph } from '@/components/marketplace/telegram-glyph'

/**
 * Site footer. Rendered by SiteShell, so it appears on every public page.
 *
 * Near-black poster: the BUY./SELL./CONNECT. stack and the community card on
 * the left, two bordered link panels beside it, the legal row, then the
 * edge-to-edge brand ticker travelling right to left at the very bottom.
 *
 * The content container repeats the header nav's own measurements EXACTLY
 * (`max-w-[1540px]` with `px-5 sm:px-8 lg:px-[clamp(24px,4vw,72px)]`, see
 * `mta-header.tsx`), so the header and the footer share one page grid and line
 * up on the same left and right edges. Change one and change the other.
 *
 * NAVIGATION RULE, and it is the reason this list changed: an INFORMATIONAL
 * footer link goes to a dedicated page that ANSWERS it, never to a filtered
 * listing view. The previous panels pointed "Verified sellers", "Latest
 * listings" and four category names at `/browse?…`, which answers nothing and
 * dead-ends a visitor who arrived at the footer looking for what Bazio is.
 * Every href below is a real page in `src/pages`. `/browse` appears exactly
 * once, as the marketplace itself.
 *
 * Bazio is the PLATFORM. Money Team Albania is one community hosted on it, and
 * the only place it is named here is the community card, which says so.
 */
const platformLinks: Array<{ label: string, href: string, icon: LucideIcon }> = [
    { label: 'How Bazio works', href: '/how-it-works', icon: Compass },
    { label: 'Marketplace', href: '/browse', icon: Store },
    { label: 'Telegram Marketplaces', href: DIRECTORY_INDEX_PATH, icon: Send },
    { label: 'For communities', href: '/for-communities', icon: Users },
    { label: 'Pricing', href: '/pricing', icon: CircleDollarSign }
]

const supportLinks: Array<{ label: string, href: string, icon: LucideIcon }> = [
    { label: 'Safety', href: '/safety', icon: ShieldCheck },
    { label: 'Report a listing', href: '/report', icon: FlagTriangleRight },
    { label: 'Contact', href: '/contact', icon: MessageCircleQuestion }
]

const legalLinks = [
    { label: 'Terms & Conditions', href: '/terms' },
    { label: 'Privacy Policy', href: '/privacy' }
]

/**
 * The founding community's own Telegram group, linked as a community rather
 * than as the platform's own channel. Bazio itself has no Telegram handle.
 */
const TELEGRAM_URL = 'https://t.me/moneyteamalbania'

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='mta-footer relative w-full overflow-hidden'>
            <div className='mx-auto w-full max-w-[1540px] px-5 sm:px-8 lg:px-[clamp(24px,4vw,72px)]'>
                <div className='border-t border-[var(--foot-line)] pb-12 pt-14 md:pb-16 md:pt-16'>
                    <div className='grid gap-12 lg:grid-cols-[1.05fr_0.9fr_0.8fr] lg:gap-10 xl:gap-14'>
                        {/* Brand */}
                        <div>
                            {/* The supplied wordmark is black artwork, so on this
                                near-black footer it rides the brand's own light
                                lockup: a cream plate, never a recolour. */}
                            <p className='flex items-center'>
                                <Link
                                    href='/'
                                    aria-label='Bazio home'
                                    className='mta-focus inline-flex rounded-mta-sm bg-canvas px-4 py-3'
                                >
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src='/images/brand/primary-logo.png'
                                        alt='Bazio'
                                        width={2172}
                                        height={724}
                                        className='h-8 w-auto select-none object-contain sm:h-9'
                                    />
                                </Link>
                            </p>

                            <h2 className='mta-foot-headline mt-6'>
                                <span className='block'>
                                    Buy.
                                </span>

                                <span className='block text-brand-red'>
                                    Sell.
                                </span>

                                <span className='block'>
                                    Connect.
                                </span>
                            </h2>

                            <p className='mta-foot-script mt-5'>
                                <span className='block'>
                                    Built around
                                </span>

                                <span className='block'>
                                    the community.
                                </span>
                            </p>

                            <HandUnderline />

                            <p className='mt-7 max-w-[26rem] text-[15px] leading-7 text-[var(--foot-muted)]'>
                                {'Bazio powers '}

                                <span className='font-semibold text-brand-red'>
                                    community marketplaces
                                </span>

                                {'. Posts in a Telegram community become listings, and every community runs its own sellers, moderation and storefront.'}
                            </p>

                            <p className='mt-7 hero-label text-[11px] tracking-[0.16em] text-brand-red'>
                                A community on Bazio
                            </p>

                            <a
                                href={TELEGRAM_URL}
                                target='_blank'
                                rel='noreferrer'
                                className='mta-focus group mt-3 flex w-full max-w-[26rem] items-center gap-4 rounded-mta-sm border border-[var(--foot-line)] bg-ink-900 px-4 py-3.5 shadow-[5px_5px_0_var(--brand-red)] transition-[transform,border-color] duration-200 ease-mta hover:-translate-y-0.5 hover:border-brand-red/70'
                            >
                                <span className='flex size-12 shrink-0 items-center justify-center rounded-mta-xs bg-brand-red-ink transition-transform duration-300 ease-mta group-hover:scale-105'>
                                    <TelegramGlyph className='size-7 text-white' />
                                </span>

                                <span className='min-w-0'>
                                    <span className='block truncate text-lg font-bold tracking-tight'>
                                        Money Team Albania
                                    </span>

                                    <span className='mt-0.5 flex items-center gap-1 text-[13px] text-[var(--foot-muted)]'>
                                        Join the group on Telegram

                                        <ArrowUpRight
                                            aria-hidden='true'
                                            className='size-3.5 transition-transform duration-200 ease-mta group-hover:translate-x-0.5'
                                        />
                                    </span>
                                </span>
                            </a>
                        </div>

                        {/* Platform */}
                        <nav aria-labelledby='footer-platform' className='rounded-mta-md border border-[var(--foot-line)] p-5 sm:p-7'>
                            <PanelHeading id='footer-platform'>
                                Platform
                            </PanelHeading>

                            <ul className='mt-6 grid gap-3'>
                                {platformLinks.map(({ label, href, icon: Icon }) => (
                                    <li key={label}>
                                        <Link
                                            href={href}
                                            onClick={href === DIRECTORY_INDEX_PATH
                                                ? () => trackEvent({ type: 'telegram_directory_entry_click', metadata: { source: 'footer' } })
                                                : undefined}
                                            className='mta-focus group flex min-h-[64px] items-center gap-3 rounded-mta-sm border border-[var(--foot-line)] bg-ink-900 px-4 py-4 shadow-[4px_4px_0_var(--brand-red)] transition-[transform,border-color,color] duration-200 ease-mta hover:-translate-y-0.5 hover:border-brand-red/70'
                                        >
                                            <Icon className='size-6 shrink-0 text-brand-red' aria-hidden='true' />

                                            <span className='flex-1 text-[15px] leading-snug'>
                                                {label}
                                            </span>

                                            <ArrowUpRight
                                                aria-hidden='true'
                                                className='size-4 shrink-0 text-[var(--foot-muted)] transition-transform duration-200 ease-mta group-hover:translate-x-1 group-hover:text-brand-red'
                                            />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        {/* Trust & support */}
                        <div className='rounded-mta-md border border-[var(--foot-line)] p-5 sm:p-7'>
                            <nav aria-labelledby='footer-support'>
                                <PanelHeading id='footer-support'>
                                    Trust
                                </PanelHeading>

                                <ul className='mt-4'>
                                    {supportLinks.map(({ label, href, icon: Icon }) => (
                                        <li key={label} className='border-b border-[var(--foot-line)]'>
                                            <Link
                                                href={href}
                                                className='mta-focus group flex min-h-14 items-center gap-3 py-3 text-[17px] transition-colors duration-200 hover:text-brand-red'
                                            >
                                                <Icon className='size-5 shrink-0 text-brand-red' aria-hidden='true' />

                                                <span className='flex-1'>
                                                    {label}
                                                </span>

                                                <ChevronRight
                                                    aria-hidden='true'
                                                    className='size-5 shrink-0 text-[var(--foot-muted)] transition-transform duration-200 ease-mta group-hover:translate-x-1 group-hover:text-brand-red'
                                                />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </nav>

                            <div className='mt-8'>
                                <p className='hero-label text-[11px] tracking-[0.16em] text-brand-red'>
                                    Legal
                                </p>

                                <ul className='mt-3 space-y-1'>
                                    {legalLinks.map(({ label, href }) => (
                                        <li key={label}>
                                            <Link
                                                href={href}
                                                className='mta-focus group inline-flex min-h-11 items-center gap-2 text-[15px] font-semibold transition-colors duration-200 hover:text-brand-red'
                                            >
                                                {label}

                                                <ArrowUpRight
                                                    aria-hidden='true'
                                                    className='size-4 text-[var(--foot-muted)] transition-transform duration-200 ease-mta group-hover:translate-x-0.5 group-hover:text-brand-red'
                                                />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Colophon. The legal links live in the Trust panel above, so
                    this row states the platform's position rather than
                    repeating them. */}
                <div className='flex flex-col gap-3 border-t border-[var(--foot-line)] py-6 text-sm text-[var(--foot-muted)] md:flex-row md:items-center md:justify-between'>
                    <p>
                        {`© ${new Date().getFullYear()} Bazio`}
                    </p>

                    <p className='max-w-xl md:text-right'>
                        Bazio is a listing and discovery platform. It is not a party to transactions between members, holds no funds and provides no escrow.
                    </p>
                </div>
            </div>

            {/* Edge-to-edge brand ticker */}
            <div className='mta-ticker-viewport w-full overflow-hidden border-y border-[var(--foot-line)] bg-ink-900 py-4 md:py-5'>
                <div className='mta-ticker flex w-max'>
                    <TickerGroup />
                    <TickerGroup aria-hidden='true' />
                </div>
            </div>

            <div aria-hidden='true' className='h-2 w-full bg-brand-red' />
        </footer>
    )
}

const PanelHeading: FC<{ id: string, children: ReactNode }> = ({ id, children }): ReactNode => {
    return (
        <h2 id={id} className='flex items-center gap-3'>
            <RoughStar />

            <span className='hero-label text-lg tracking-[0.06em]'>
                {children}
            </span>
        </h2>
    )
}

/** The small hand-drawn red star that marks a footer panel. */
const RoughStar: FC = (): ReactNode => {
    return (
        <svg
            viewBox='0 0 32 32'
            className='size-7 shrink-0 text-brand-red'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.7'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
            focusable='false'
        >
            <path d='M16 3.5 6.2 27.4 27 12.1 4.9 12.6 25.6 27.8Z' />
        </svg>
    )
}

/** The imperfect red brush rule under the italic line. */
const HandUnderline: FC = (): ReactNode => {
    return (
        <svg
            viewBox='0 0 300 14'
            preserveAspectRatio='none'
            className='mt-3 h-3 w-[min(21rem,100%)] text-brand-red'
            aria-hidden='true'
            focusable='false'
        >
            <path
                d='M2 11.4C48 6.6 96 3.6 148 4.2c44 .5 90 2.4 150 1.1-58 4.4-116 5.6-174 5.2-40-.3-80-.4-122 .9Z'
                fill='currentColor'
            />
        </svg>
    )
}

/** One repeat of the ticker content. Two of these make the seamless loop. */
const TickerGroup: FC<{ 'aria-hidden'?: 'true' }> = (props): ReactNode => {
    return (
        <div className='flex shrink-0 items-center' {...props}>
            {[0, 1, 2, 3].map((index) => (
                <div key={index} className='flex shrink-0 items-center gap-[clamp(14px,1.6vw,28px)] pr-[clamp(14px,1.6vw,28px)]'>
                    <span className='mta-ticker-word text-[var(--foot-fg)]'>
                        Bazio
                    </span>

                    {/* The app icon is the one supplied asset that is already a
                        red tile with a white mark, so it is the mark that holds
                        up on this near-black band without any recolouring. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src='/images/brand/app-icon.png'
                        alt=''
                        width={1254}
                        height={1254}
                        aria-hidden='true'
                        className='size-[clamp(38px,4vw,64px)] shrink-0 select-none rounded-mta-xs object-contain'
                    />
                </div>
            ))}
        </div>
    )
}
